import { inject, Injectable, signal } from '@angular/core';
import { IStudent } from '../interfaces/IStudent';
import { IExamStudent } from '../interfaces/IExamStudent';
import { db } from '../db/AppDatabase';
import { IScanItem, ScanDirection, scanStatus } from '../interfaces/IScanItem';
import { firstValueFrom } from 'rxjs';
import { ExamSessionService } from '../_Services/apih/exam-session-service';
import { IExamStudentResult, IScan, IScans } from '../_Models/iexam-session';
import { ExamsService } from './exams-service';

@Injectable({
  providedIn: 'root',
})
export class StudentsService {

  private _studentsForExam = signal<IExamStudentVM[] | null>(null);
  public StudentsForExam = this._studentsForExam.asReadonly();

  private _studentsUpdated = signal<number>(0);
  public StudentsUpdated = this._studentsUpdated.asReadonly();

  public examsFromAPI = inject(ExamSessionService);
  private examsSvc = inject(ExamsService);
  private _loadingFromAPI = signal(false);
  public LoadingFromAPI = this._loadingFromAPI.asReadonly();

  async loadStudentsForExam(examId: string) {
    this.ClearStudentsForExam();

    // 1. Koppeltabel voor dit examen ophalen
    const links: IExamStudent[] = await db.examStudents
      .where('examId')
      .equals(examId)
      .toArray();

    if (links.length === 0) {
      this._studentsForExam.set(null);
      return;
    }

    // 2. Alle betrokken studenten ophalen
    const studentIds = links.map(l => l.studentId);
    const students: IStudent[] = await db.students
      .where('id')
      .anyOf(studentIds)
      .toArray();

    // 3. JOIN: link + student → VM
    const result: IExamStudentVM[] = links
      .map(link => {
        const student = students.find(s => s.id === link.studentId);
        if (!student) return undefined; // zou normaal niet mogen gebeuren

        return {
          id: link.id,
          sId: student.id,
          sNr : student.sNr,
          lastName : student.lastName,
          firstName : student.firstName,
          group: link.group,
          scanItems: [],
        } as IExamStudentVM;
      })
      .filter((x): x is IExamStudentVM => !!x);

    let scans: IScanItem[] = await db.scanHistory
      .where('examId')
      .equals(examId)
      .sortBy('date');

    //filter only valid scans for the student overview
    scans = scans.filter(s => s.status == scanStatus.ValidScan);

    const scansByStudent = new Map<string, IScanItem[]>();

    for (const scan of scans) {
      const key = scan.examStudentId;

      if (key != undefined) {
        if (!scansByStudent.has(key)) {
          scansByStudent.set(key, []);
        }

        scansByStudent.get(key)!.push(scan);
      }
    }

    for (const examStudent of result) {
      examStudent.scanItems = scansByStudent.get(examStudent.id) ?? [];
    }

    // 4. Optioneel: sorteren (bv. eerst op group, dan op achternaam en dan op voornaam)
    result.sort((a, b) => {
      const g = a.group.localeCompare(b.group);
      if (g !== 0) return g;
      const h = a.lastName.localeCompare(b.lastName);
      if (h != 0) return h;
      return a.firstName.localeCompare(b.firstName);
    });

    this._studentsForExam.set(result);
  }

  ClearStudentsForExam() {
    //image URL cleanup
    let list = this._studentsForExam();
    if (list != null) {
      for (const s of list) {
        if (s.studentPhotoURL && s.studentPhotoURL.startsWith('blob:')) {
          URL.revokeObjectURL(s.studentPhotoURL);
          s.studentPhotoURL = undefined;
        }
        if (s.checkinPhotoURL && s.checkinPhotoURL.startsWith('blob:')) {
          URL.revokeObjectURL(s.checkinPhotoURL);
          s.checkinPhotoURL = undefined;
        }
        if (s.checkoutPhotoURL && s.checkoutPhotoURL.startsWith('blob:')) {
          URL.revokeObjectURL(s.checkoutPhotoURL);
          s.checkoutPhotoURL = undefined;
        }
      }
    }

    this._studentsForExam.set(null);
  }

  RefreshTick() {
    this._studentsUpdated.update(n => n + 1);
  }

  markScansAsSynced(ids: string[]): void {
    const idSet = new Set(ids);
    const list = this._studentsForExam();
    if (!list) return;
    for (const student of list) {
      for (const scan of student.scanItems) {
        if (idSet.has(scan.id)) {
          scan.synced = true;
        }
      }
    }
    this.RefreshTick();
  }

  /** Voegt een scan toe van een andere scanner (SignalR) aan de juiste student
   *  in de in-memory lijst, zonder alles te herladen. */
  addScanToStudent(scan: IScanItem): void {
    const list = this._studentsForExam();
    if (!list || scan.status !== scanStatus.ValidScan) return;
    const student = list.find(s => s.id === scan.examStudentId);
    if (!student) return;
    student.scanItems.push(scan);
    student.scanItems.sort((a, b) => a.date.getTime() - b.date.getTime());
    this.RefreshTick();
  }

  async GetStudentById(id: string) {
    let student = await db.students.get(id)
    return student;
  }

  async GetStudentBySNumber(sNr: string) {
    let paddedSNr = sNr.padStart(6, '0');
    let student = await db.students
        .where('sNr')
        .equals(paddedSNr)
        .first();       
    return student;
  }

  async AddStudent(student: IStudent) {
    await db.students.put(student); //put wil overwrite if already present
  }

  async GetStudentForExam(examId: string, id: string) {
    return await db.examStudents.get(examId + "-" + id);
  }

  async AddStudentToExam(examStudent: IExamStudent) {
    examStudent.id = examStudent.examId + "-" + examStudent.studentId;
    await db.examStudents.put(examStudent);
  }

  async DeleteAllStudensForExam(examId: string) {
    await db.examStudents.where('examId').equals(examId).delete();
  }

  async refreshStudentsFromAPI(examId: string) {
    this._loadingFromAPI.set(true);
    let result: IExamStudentResult[] = [];
    try {
      result = await firstValueFrom(this.examsFromAPI.GetAllStudentsForExam(examId));
    } catch (error) {
      console.log(error)
    }

    for (let item of result) {
      for (let student of item.students) {
        //this is kind of hacky way...
        let sNr = student.userName.substring(1, student.userName.indexOf('@')).padStart(6, '0');
        let existing = await this.GetStudentById(student.userId);
        if (existing == null) {
          let photo: Blob | undefined = undefined;
          if (student.base64Image) {
            const resp = await fetch(student.base64Image);
            const blob = await resp.blob();
            if (blob.size > 0) photo = blob;
          }

          await this.AddStudent({
            id: student.userId,
            sNr : sNr,
            lastName: student.lastName,
            firstName: student.firstName,
            photo: photo
          })
          // console.log("student created from API " + student.lastName);
        }

        let examStudent = await this.GetStudentForExam(examId, student.userId);
        if (examStudent == null) {
          await this.AddStudentToExam(
            {
              id: `${examId}-${student.userId}`,
              examId: examId,
              group: item.classGroup,
              studentId: student.userId
            }
          )
          // console.log("student added to exam: " + student.lastName);
        }
      }
    }

    try {
      const apiScans: IScan[] = await firstValueFrom(this.examsFromAPI.GetScansForExam(examId));
      for (const scan of apiScans) {
        const examStudentId = `${examId}-${scan.studentUserID}`;
        const scanDate = new Date(scan.scanDate);

        // Sla enkel op als er nog geen lokale scan met dit ID bestaat
        const existing = await db.scanHistory.get(scan.appScanID);
        if (!existing) {
          await db.scanHistory.put({
            id: scan.appScanID,
            examId: examId,
            examStudentId: examStudentId,
            studentId: scan.studentUserID,
            scanCode: scan.scanCode,
            status: scan.scanStatus,
            direction: scan.scanDirection,
            date: scanDate,
            method: scan.scanMethod,
            scannerName: scan.scannerName,
            synced: true,
          } as IScanItem);
        }
      }
    } catch (error) {
      console.log(error)
    }
    // console.log("examstudents refresh from API completed");
    this._loadingFromAPI.set(false);

    const count = await db.examStudents.where('examId').equals(examId).count();
    const exam = this.examsSvc.getExamById(examId);
    if (exam) {
      await db.exams.update(examId, { studentsCount: count });
      await this.examsSvc.loadUpcomingExams();
    }

    await this.loadStudentsForExam(examId);
  }
}


export interface IExamStudentVM {
  id: string;
  lastName: string;
  firstName: string;
  sId: string;
  sNr : string;
  group: string;        // klasgroep
  scanItems: IScanItem[];
  studentPhotoURL?: string;
  checkinPhotoURL?: string;
  checkoutPhotoURL?: string;
}
